/**
 * Explore Unknown — battle screen (BattleUI) backgrounds by global mission index.
 * Backgrounds live under `public/unknown_backgrounds/` and are excluded from faction random rotation.
 * Add a row per mission that should use fixed art; unlisted indices use normal faction-based rotation.
 */

const EXPLORE_UNKNOWN_MISSION_BATTLE_BACKGROUNDS = {
  1: 'woods_a.jpg',
  2: 'lake_a.jpg',
  3: 'dinner_woods.jpg',
  4: 'pond.jpg',
  5: 'arc.jpg',
  6: 'cave1.jpg',
  7: 'cave2.jpg',
  8: 'cave3.jpg',
  9: 'cave4.jpg',
  10: 'images/boss1.mp4',
  11: 'desert1.jpg',
  12: 'desert2.jpg',
  13: 'desert3.jpg',
  14: 'desert4.jpg',
  15: 'desert5.jpg',
  16: 'images/boss2.mp4',
  17: 'fair1.jpg',
  18: 'fair2.jpg',
  19: 'fair3.jpg',
  20: 'fair4.jpg',
  21: 'fair5.jpg',
  22: 'fair6.jpg',
  23: 'images/peacock.jpg',
  24: 'lavender1.jpg',
  25: 'lavender2.jpg',
  26: 'lavender3.jpg',
  27: 'images/boss4.mp4',
  28: 'lavender4.jpg',
  29: 'treetop_bg.jpg',
  30: 'open_sea_1.jpg',
  31: 'open_sea_2.jpg',
  32: 'open_sea_3.jpg',
  33: 'images/kraken.mp4',
  34: 'open_sea_4.jpg',
  35: 'pixelpast1.jpg',
  36: 'pixelpast2.jpg',
  37: 'pixelpast3.jpg',
  38: 'pixelpast4.jpg',
  39: 'pixelpast5.jpg',
  40: 'pixelpast6.jpg',
  41: 'kendrex1.jpg',
  42: 'kendrex2.jpg',
  43: 'kendrex3.jpg',
  44: 'kendrex4.jpg',
  45: 'kendrex5.jpg',
  46: 'kendrex6.jpg',

};

/** First global mission index on each map surface (skip-world choice preview art). */
const EXPLORE_UNKNOWN_SURFACE_FIRST_MISSION_INDEX = Object.freeze({
  arrival: 1,
  cave: 6,
  desert: 11,
  fair: 17,
  lavender: 24,
  treetop: 29,
  boat: 30,
  pixelpast: 35,
  kendrex: 41,
});

/**
 * @param {string} surface — `arrival` | `cave` | `desert` | `fair` | `lavender` | `treetop` | `boat` | `pixelpast` | `kendrex`
 * @returns {string} URL path for the first mission battle background on that surface (e.g. `/unknown_backgrounds/fair1.jpg`)
 */
function getExploreUnknownSurfaceFirstMissionPreviewUrl(surface) {
  const idx = EXPLORE_UNKNOWN_SURFACE_FIRST_MISSION_INDEX[surface];
  if (idx == null) {
    throw new Error(`Explore Unknown: no first mission index for surface (${String(surface)})`);
  }
  const backgroundPath = tryGetExploreUnknownBattleBackgroundPath(idx);
  if (!backgroundPath) {
    throw new Error(
      `Explore Unknown: no battle background for surface ${String(surface)} mission ${idx}`
    );
  }
  if (/\.(mp4|webm)$/i.test(backgroundPath)) {
    throw new Error(
      `Explore Unknown: first mission preview must be an image, not video (${backgroundPath})`
    );
  }
  return resolveBattleBackgroundCssUrl(backgroundPath);
}

/**
 * @param {unknown} missionIndex — `gameState.exploreUnknownMissionIndex`
 * @returns {string | null} e.g. `unknown_backgrounds/woods_a.jpg`, or null when this mission has no fixed art (use faction rotation)
 */
function tryGetExploreUnknownBattleBackgroundPath(missionIndex) {
  const idx = Number(missionIndex);
  if (!Number.isInteger(idx) || idx < 1) {
    return null;
  }
  const file = EXPLORE_UNKNOWN_MISSION_BATTLE_BACKGROUNDS[idx];
  if (!file) {
    return null;
  }
  if (file.startsWith('images/')) {
    return file;
  }
  return `unknown_backgrounds/${file}`;
}

/**
 * @param {string} backgroundImage — either an `/images/` filename (e.g. `snow.jpg`) or `unknown_backgrounds/...`
 * @returns {string} path for CSS `url(...)` (leading slash, no encode)
 */
function resolveBattleBackgroundCssUrl(backgroundImage) {
  if (!backgroundImage || typeof backgroundImage !== 'string') {
    throw new Error('resolveBattleBackgroundCssUrl: backgroundImage is required');
  }
  if (backgroundImage.startsWith('unknown_backgrounds/')) {
    return `/${backgroundImage}`;
  }
  if (backgroundImage.startsWith('images/')) {
    return `/${backgroundImage}`;
  }
  return `/images/${backgroundImage}`;
}

/**
 * Boss mission tactical loss: darkness increase before retry (absolute percentage points).
 * Must match `EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT` in `modules/exploreUnknownMissions.js`.
 */
const EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT = 30;

/**
 * Rewrite Experience mission loss retry: darkness increase before retry (absolute percentage points).
 * Must match `REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT` in `modules/exploreUnknownRewriteExperience.js`.
 */
const EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT = 10;

module.exports = {
  tryGetExploreUnknownBattleBackgroundPath,
  resolveBattleBackgroundCssUrl,
  getExploreUnknownSurfaceFirstMissionPreviewUrl,
  EXPLORE_UNKNOWN_BOSS_LOSS_DARKNESS_INCREMENT_PERCENT,
  EXPLORE_UNKNOWN_REWRITE_EXPERIENCE_LOSS_DARKNESS_INCREMENT_PERCENT,
};
